/**
 * 広告を作る 4 段（/money/ads/new・作りかけの続きは ?c=<広告の id>・2026-10-22 money/self-serve-ads-dash）。
 * 設計 §6 の ①〜④: ① 何を広告する（自分の公開中のゲーム）② AI の案（バナーと一文・作り直し）③ いくら使う ④ 確認して確認に出す。
 *
 * 流れと口（約束 §2）:
 *   ① ゲームを選ぶ（GET targets = 広告にできるかと理由・2026-10-22 ssa-finish）→ POST check-url（公開中・一般向け・自分の・止められていない）
 *      →「続ける」で POST campaigns（draft・1 日の上限は既定）
 *   ② バナー: AI で作る（POST campaigns/:id/suggest・原価は前払いから）か、自分の画像を上げる（PUT campaigns/:id/banners/:size・components/BannerUpload.tsx）。
 *      一文を直す = PATCH tagline。作り直す = suggest をもう 1 回
 *      2026-10-22 ssa-founder-1022（founder「AI で作ってもいいしがクレジットから引く。アップロードでもいい」「AI の利用規約はいらない」）:
 *      以前は ① の後すぐ AI の案を作り、初めての人には AI の規約の同意のチェックを出していた → 作り方を ② で選ぶ形にし、同意のチェックは外した
 *   ③ PATCH dailyCapCents / totalCapCents
 *   ④ POST submit（機械と AI の中身のチェック → reviewing。だめなら 422 review_rejected + 理由・AI が使えない 503・回数の上限 429）→ 数字の画面へ
 * WHY 段ごとにサーバーへ保存するか: 途中で閉じても一覧に「作りかけ」が残り、続きから戻れる（resumeStep）。AI の案はお金がかかるので失くさない。
 *
 * 言葉: 非エンジニア向け・英語が先（i18n en.selfAds.ts）。「無料」と書かない（確認で断られてもかかるのは AI の原価だけ = その事実を書く）。
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { ApiError } from '../../api';
import { useResource } from '../../hooks';
import { errorText, useT, type MessageKey } from '../../i18n';
import { money } from '../../money';
import { isMoneyNavHidden } from '../../launchHidden';
import { ErrorState, PageHeader, Spinner } from '../../components/ui';
import { AdBanner } from '../../components/AdBanner';
import { BannerUpload, UPLOAD_ERRORS } from '../../components/BannerUpload';
import {
  checkUrl,
  createCampaign,
  TARGETS_PATH,
  getCampaignDetail,
  isOff,
  SETTINGS_PATH,
  submitCampaign,
  suggestCreative,
  updateCampaign
} from '../../selfAdsApi';
import {
  ADS_PATH,
  FALLBACK_SETTINGS,
  TAGLINE_MAX,
  WIZARD_STEPS,
  adPath,
  balanceShort,
  capProblem,
  dailyCapOptions,
  daysCovered,
  hasBanner,
  isKnownReason,
  resumeStep,
  taglineLength,
  totalCapOptions,
  viewsFor,
  type Campaign,
  type CheckedTarget,
  type TargetOption,
  type SelfAdSettings,
  type WizardStep
} from '../../selfAds';

/** このページで出しうる口のエラーの言葉（errorText の overrides。汎用の invalid_state 等と混ぜない） */
export const SSA_ERRORS: Partial<Record<string, MessageKey>> = {
  invalid_url: 'ssa.err.invalid_url',
  target_not_kumo_game: 'ssa.err.target_not_kumo_game',
  target_not_owned: 'ssa.err.target_not_owned',
  target_not_public: 'ssa.err.target_not_public',
  target_not_general: 'ssa.err.target_not_general',
  target_suspended: 'ssa.err.target_suspended',
  invalid_daily_cap: 'ssa.err.invalid_daily_cap',
  invalid_total_cap: 'ssa.err.invalid_total_cap',
  banner_asset_invalid: 'ssa.err.banner_asset_invalid',
  review_rejected: 'ssa.err.review_rejected',
  insufficient_ad_credits: 'ssa.err.insufficient_ad_credits',
  invalid_state: 'ssa.err.invalid_state',
  self_ads_stopped: 'ssa.err.self_ads_stopped',
  campaign_not_found: 'ssa.err.campaign_not_found',
  route_missing: 'ssa.err.route_missing',
  suggest_failed: 'ssa.err.suggest_failed',
  // AI の中身のチェック（2026-10-22 ssa-founder-1022）: 上流が使えない = 少し後に・1 日の回数の上限 = 明日
  review_unavailable: 'ssa.err.review_unavailable',
  review_limit: 'ssa.err.review_limit',
  ...UPLOAD_ERRORS
};

const STEP_KEYS: Record<WizardStep, MessageKey> = { 1: 'ssa.step.what', 2: 'ssa.step.banner', 3: 'ssa.step.budget', 4: 'ssa.step.review' };

export function AdsNewPage() {
  const { t } = useT();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const resumeId = params.get('c');
  const settingsR = useResource<SelfAdSettings>(SETTINGS_PATH);
  const s: SelfAdSettings | null = settingsR.data;
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [step, setStep] = useState<WizardStep>(1);
  const [loadErr, setLoadErr] = useState<ApiError | null>(null);

  // 作りかけの続き: 広告を読んで段を決める（バナーがある = ③ から）
  useEffect(() => {
    if (!resumeId || campaign?.id === resumeId) return;
    getCampaignDetail(resumeId)
      .then((d) => {
        setCampaign(d.campaign);
        setStep(resumeStep(d.campaign));
      })
      .catch((e: ApiError) => setLoadErr(e));
  }, [resumeId, campaign?.id]);

  // 段を変えたら画面の上へ（スマホで次の段の頭が見えない、を防ぐ）
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [step]);

  if (isOff(settingsR.error)) return <Navigate404 />;

  const onCreated = (c: Campaign) => {
    setCampaign(c);
    // URL に id を入れる = 再読み込み・戻るで作りかけを失くさない
    setParams({ c: c.id }, { replace: true });
  };

  return (
    <>
      <PageHeader eyebrow={t('ssa.title')} title={t('ssa.new.title')}>
        <Link to={ADS_PATH} className="btn btn-ghost btn-sm">
          {t('ssa.new.back')}
        </Link>
      </PageHeader>

      <ol className="ssa-steps" aria-label={t('ssa.new.title')}>
        {WIZARD_STEPS.map((n) => (
          <li key={n} className={n === step ? 'is-current' : n < step ? 'is-done' : ''} aria-current={n === step ? 'step' : undefined}>
            <span className="ssa-step-n">{n}</span>
            <span className="ssa-step-label">{t(STEP_KEYS[n])}</span>
          </li>
        ))}
      </ol>

      {loadErr && <ErrorState error={loadErr} what={t('ssa.new.title')} />}
      {resumeId && !campaign && !loadErr && <Spinner />}

      {s?.allStopped && (
        <p className="alert alert-warn" role="status">
          {t('ssa.allStopped')}
        </p>
      )}

      {step === 1 && (!resumeId || campaign) && <StepWhat settings={s} campaign={campaign} onCreated={onCreated} onNext={() => setStep(2)} />}
      {step === 2 && campaign && <StepBanner settings={s} campaign={campaign} onChange={setCampaign} onBack={() => setStep(1)} onNext={() => setStep(3)} />}
      {step === 3 && campaign && <StepBudget settings={s} campaign={campaign} onChange={setCampaign} onBack={() => setStep(2)} onNext={() => setStep(4)} />}
      {step === 4 && campaign && (
        <StepReview
          settings={s}
          campaign={campaign}
          onBack={() => setStep(3)}
          onEdit={(n) => setStep(n)}
          onSubmitted={(c) => navigate(adPath(c.id), { state: { submitted: true } })}
        />
      )}
    </>
  );
}

/** フラグ off で URL を打って来た: 一覧へ（一覧が「まだ使えません」を出す） */
function Navigate404() {
  const navigate = useNavigate();
  useEffect(() => {
    void navigate(ADS_PATH, { replace: true });
  }, [navigate]);
  return null;
}

function StepCard({ title, lead, children, actions }: { title: string; lead?: ReactNode; children: ReactNode; actions: ReactNode }) {
  return (
    <section className="card ssa-step-card">
      <h2 className="card-title">{title}</h2>
      {lead && <p className="muted small ssa-step-lead">{lead}</p>}
      {children}
      <div className="form-actions ssa-step-actions">{actions}</div>
    </section>
  );
}

// ------------------------------------------------------------------ ① 何を広告する

function StepWhat({
  settings,
  campaign,
  onCreated,
  onNext
}: {
  settings: SelfAdSettings | null;
  campaign: Campaign | null;
  onCreated: (c: Campaign) => void;
  onNext: () => void;
}) {
  const { t } = useT();
  // 広告にできる自分のアプリの一覧（GET targets・2026-10-22 ssa-finish・要望 5）。できない物も理由つきで出す = 選んでから断られない
  const projects = useResource<{ targets: TargetOption[] }>(campaign ? null : TARGETS_PATH);
  const [picked, setPicked] = useState<string | null>(null);
  const [pasted, setPasted] = useState('');
  const [checking, setChecking] = useState(false);
  const [target, setTarget] = useState<CheckedTarget | null>(null);
  const [checkErr, setCheckErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // 行き先は作った後は変えない（確認した版に結び付く・約束 target_deployment_id）。別のゲームは新しい広告で
  if (campaign) {
    return (
      <StepCard title={t('ssa.what.title')} actions={<button type="button" className="btn btn-primary" onClick={onNext}>{t('ssa.next')}</button>}>
        <p>
          <strong>{campaign.targetName}</strong> <span className="mono small muted">{campaign.targetUrl}</span>
        </p>
        <p className="small muted">{t('ssa.what.fixed')}</p>
      </StepCard>
    );
  }

  const check = async (url: string) => {
    setChecking(true);
    setTarget(null);
    setCheckErr(null);
    try {
      const r = await checkUrl(url);
      setTarget(r.target);
    } catch (e) {
      setCheckErr(errorText(e as ApiError, SSA_ERRORS));
    } finally {
      setChecking(false);
    }
  };

  const pick = async (p: TargetOption) => {
    setPicked(p.projectId);
    setPasted('');
    setTarget(null);
    setCheckErr(null);
    // 一覧が「できない」と言った物は理由だけ出す。できる物は check-url で確かめ直す（一覧を開いた後に公開をやめた、を拾う。判定はサーバーの 1 か所）
    if (!p.ok || !p.url) {
      const key = SSA_ERRORS[p.reason ?? ''];
      setCheckErr(key ? t(key) : t('ssa.err.target_not_public'));
      return;
    }
    await check(p.url);
  };

  const make = async () => {
    if (!target) return;
    setBusy(true);
    setErr(null);
    try {
      const { campaign: c } = await createCampaign({ url: target.url, dailyCapCents: (settings ?? FALLBACK_SETTINGS).dailyDefaultCents });
      onCreated(c);
      // バナーは ② で作り方を選ぶ（AI で作る / 自分の画像を上げる）= ここでは AI を呼ばない（選ぶ前に原価をかけない）
      onNext();
    } catch (e) {
      setErr(errorText(e as ApiError, SSA_ERRORS));
    } finally {
      setBusy(false);
    }
  };

  const list = projects.data?.targets ?? [];
  return (
    <StepCard
      title={t('ssa.what.title')}
      lead={t('ssa.what.lead')}
      actions={
        <button type="button" className="btn btn-primary" disabled={!target || busy || settings?.allStopped} onClick={() => void make()}>
          {busy ? t('ssa.what.making') : t('ssa.what.make')}
        </button>
      }
    >
      {projects.loading && !projects.data && <Spinner />}
      {projects.error && <ErrorState error={projects.error} onRetry={projects.reload} />}
      {projects.data && list.length === 0 && <p className="muted">{t('ssa.what.noGames')}</p>}
      {list.length > 0 && (
        <fieldset className="choice-group ssa-games">
          <legend>{t('ssa.what.pick')}</legend>
          {list.map((p) => (
            <label key={p.projectId} className={`choice ${picked === p.projectId ? 'is-active' : ''}`}>
              <input type="radio" name="ssa-game" checked={picked === p.projectId} onChange={() => void pick(p)} />
              <span>
                <strong>{p.name}</strong>
                <span className="small muted mono">{p.slug}</span>
                {!p.ok && <span className="small muted">{t(SSA_ERRORS[p.reason ?? ''] ?? 'ssa.err.target_not_public')}</span>}
              </span>
            </label>
          ))}
        </fieldset>
      )}

      <details className="ssa-paste">
        <summary className="small">{t('ssa.what.pasteToggle')}</summary>
        <form
          className="form-inline"
          onSubmit={(e) => {
            e.preventDefault();
            if (pasted.trim()) {
              setPicked(null);
              void check(pasted.trim());
            }
          }}
        >
          <label className="field field-grow">
            <span className="sr-only">{t('ssa.what.pasteLabel')}</span>
            <input
              type="url"
              inputMode="url"
              placeholder="https://"
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
              autoCapitalize="off"
              spellCheck={false}
            />
          </label>
          <button type="submit" className="btn btn-ghost" disabled={!pasted.trim() || checking}>
            {t('ssa.what.check')}
          </button>
        </form>
      </details>

      <div aria-live="polite" className="ssa-check">
        {checking && <Spinner label={t('ssa.what.checking')} />}
        {target && (
          <p className="alert alert-ok">
            <span>✓ {t('ssa.what.ok')}</span>
          </p>
        )}
        {checkErr && <p className="alert alert-error">{checkErr}</p>}
      </div>

      {err && <p className="alert alert-error">{err}</p>}
    </StepCard>
  );
}

// ------------------------------------------------------------------ ② AI の案

function StepBanner({
  settings,
  campaign,
  onChange,
  onBack,
  onNext
}: {
  settings: SelfAdSettings | null;
  campaign: Campaign;
  onChange: (c: Campaign) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const { t } = useT();
  const [hint, setHint] = useState('');
  const [line, setLine] = useState(campaign.tagline ?? '');
  const [busy, setBusy] = useState<'ai' | 'save' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => setLine(campaign.tagline ?? ''), [campaign.tagline]);
  const len = taglineLength(line);
  const dirty = line.trim() !== (campaign.tagline ?? '');

  // AI で作る（作り直しも同じ）。原価は AI の利用料として前払いから。AI の利用規約の同意は求めない（founder 10/22）
  const makeWithAi = async () => {
    setBusy('ai');
    setErr(null);
    try {
      onChange((await suggestCreative(campaign.id, hint.trim() || undefined)).campaign);
    } catch (e) {
      setErr(errorText(e as ApiError, SSA_ERRORS));
    } finally {
      setBusy(null);
    }
  };
  const saveLine = async () => {
    setBusy('save');
    setErr(null);
    try {
      onChange((await updateCampaign(campaign.id, { tagline: line.trim() })).campaign);
      setSaved(true);
    } catch (e) {
      setErr(errorText(e as ApiError, SSA_ERRORS));
    } finally {
      setBusy(null);
    }
  };
  const next = async () => {
    // 直した一文を保存してから次へ（押し忘れで消えない）
    if (dirty) {
      try {
        onChange((await updateCampaign(campaign.id, { tagline: line.trim() })).campaign);
      } catch (e) {
        setErr(errorText(e as ApiError, SSA_ERRORS));
        return;
      }
    }
    onNext();
  };

  const cost = settings?.suggestCostCents;
  const has = hasBanner(campaign);
  const aiLabel = busy === 'ai' ? t('ssa.banner.making') : has ? (cost ? t('ssa.banner.againCost', { cost: money(cost, 'usd') }) : t('ssa.banner.again')) : cost ? t('ssa.banner.aiMakeCost', { cost: money(cost, 'usd') }) : t('ssa.banner.aiMake');
  return (
    <StepCard
      title={t('ssa.banner.title')}
      lead={t('ssa.banner.lead')}
      actions={
        <>
          <button type="button" className="btn btn-ghost" onClick={onBack}>
            {t('ssa.back')}
          </button>
          <button type="button" className="btn btn-primary" disabled={busy !== null || !has || len > TAGLINE_MAX} onClick={() => void next()}>
            {t('ssa.banner.use')}
          </button>
        </>
      }
    >
      <div className="ad-banners">
        <AdBanner campaignId={campaign.id} size={320} assetId={campaign.banner320AssetId} tagline={line || campaign.tagline} name={campaign.targetName} />
        <AdBanner campaignId={campaign.id} size={728} assetId={campaign.banner728AssetId} tagline={line || campaign.tagline} name={campaign.targetName} />
      </div>

      {/* 作り方は 2 つ（founder 10/22「AI で作ってもいいし…アップロードでもいい」）。どちらで作っても後から入れ替えられる */}
      <div className="ssa-ways">
        <section className="ssa-way" aria-labelledby="ssa-way-ai">
          <h3 id="ssa-way-ai">{t('ssa.banner.aiTitle')}</h3>
          <p className="small muted">{t('ssa.banner.aiHelp')}</p>
          <label className="field">
            <span>
              {t('ssa.what.hint')} <small className="muted">{t('ssa.optional')}</small>
            </span>
            <input value={hint} maxLength={120} onChange={(e) => setHint(e.target.value)} placeholder={t('ssa.what.hintPlaceholder')} />
          </label>
          <div>
            <button type="button" className="btn btn-ghost" disabled={busy !== null} onClick={() => void makeWithAi()}>
              {aiLabel}
            </button>
          </div>
        </section>
        <section className="ssa-way" aria-labelledby="ssa-way-upload">
          <h3 id="ssa-way-upload">{t('ssa.banner.uploadTitle')}</h3>
          <BannerUpload campaign={campaign} onChange={onChange} disabled={busy !== null} />
        </section>
      </div>
      <p className="small muted">{t('ssa.banner.checkNote')}</p>

      <form
        className="ssa-line"
        onSubmit={(e) => {
          e.preventDefault();
          if (dirty && len <= TAGLINE_MAX) void saveLine();
        }}
      >
        <label className="field">
          <span className="field-head">
            {t('ssa.banner.line')}
            <small className={len > TAGLINE_MAX ? 'field-error' : 'muted'}>{t('ssa.banner.count', { n: len, max: TAGLINE_MAX })}</small>
          </span>
          <input
            value={line}
            onChange={(e) => {
              setLine(e.target.value);
              setSaved(false);
            }}
            placeholder={campaign.targetName}
            aria-invalid={len > TAGLINE_MAX}
          />
        </label>
        <p className="small muted">{t('ssa.banner.lineHelp')}</p>
        {dirty && (
          <button type="submit" className="btn btn-ghost btn-sm" disabled={busy !== null || len > TAGLINE_MAX}>
            {t('ssa.banner.saveLine')}
          </button>
        )}
        {saved && !dirty && <span className="small muted">{t('ssa.saved')}</span>}
      </form>
      {err && <p className="alert alert-error">{err}</p>}
    </StepCard>
  );
}

// ------------------------------------------------------------------ ③ いくら使う

function StepBudget({
  settings,
  campaign,
  onChange,
  onBack,
  onNext
}: {
  settings: SelfAdSettings | null;
  campaign: Campaign;
  onChange: (c: Campaign) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const { t } = useT();
  const range = settings ?? FALLBACK_SETTINGS;
  const price = campaign.priceCentsPerMille;
  const [daily, setDaily] = useState(campaign.dailyCapCents);
  const [total, setTotal] = useState<number | null>(campaign.totalCapCents);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dailyOpts = useMemo(() => dailyCapOptions(range), [range]);
  const totalOpts = useMemo(() => totalCapOptions(daily), [daily]);
  const problem = capProblem(daily, total, range);

  const next = async () => {
    setBusy(true);
    setErr(null);
    try {
      if (daily !== campaign.dailyCapCents || total !== campaign.totalCapCents) {
        onChange((await updateCampaign(campaign.id, { dailyCapCents: daily, totalCapCents: total })).campaign);
      }
      onNext();
    } catch (e) {
      setErr(errorText(e as ApiError, SSA_ERRORS));
    } finally {
      setBusy(false);
    }
  };

  return (
    <StepCard
      title={t('ssa.budget.title')}
      actions={
        <>
          <button type="button" className="btn btn-ghost" onClick={onBack}>
            {t('ssa.back')}
          </button>
          <button type="button" className="btn btn-primary" disabled={busy || problem !== null} onClick={() => void next()}>
            {t('ssa.next')}
          </button>
        </>
      }
    >
      <div className="form-grid ssa-budget">
        <label className="field">
          <span>{t('ssa.budget.daily')}</span>
          <select
            value={daily}
            onChange={(e) => {
              const d = Number(e.target.value);
              setDaily(d);
              // 総額が 1 日の上限より小さくなったら外す（サーバーの invalid_total_cap を先に避ける）
              if (total !== null && total < d) setTotal(null);
            }}
          >
            {dailyOpts.map((c) => (
              <option key={c} value={c}>
                {/* 「おすすめ」は札に入れない（スマホの select の幅で切れた・見た目の確認 2026-10-22）= 下の説明の文に書く */}
                {t('ssa.budget.option', { amount: money(c, 'usd'), views: viewsFor(c, price) })}
              </option>
            ))}
          </select>
          <small className="muted">{t('ssa.budget.dailyHelp', { suggested: money(range.dailyDefaultCents, 'usd') })}</small>
        </label>
        <label className="field">
          <span>{t('ssa.budget.total')}</span>
          <select value={total ?? ''} onChange={(e) => setTotal(e.target.value === '' ? null : Number(e.target.value))}>
            {totalOpts.map((c) => (
              <option key={c ?? 'none'} value={c ?? ''}>
                {c === null ? t('ssa.budget.noLimit') : t('ssa.budget.totalOption', { amount: money(c, 'usd'), views: viewsFor(c, price) })}
              </option>
            ))}
          </select>
          <small className="muted">{t(total === null ? 'ssa.budget.totalHelpNone' : 'ssa.budget.totalHelp')}</small>
        </label>
      </div>

      <dl className="kv ssa-budget-kv">
        <dt>{t('ssa.budget.price')}</dt>
        <dd>{t('ssa.budget.priceValue', { price: money(price, 'usd') })}</dd>
        <dt>{t('ssa.budget.balance')}</dt>
        <dd>
          {settings ? <strong>{money(settings.spendableCents, 'usd')}</strong> : '—'}
          {settings && settings.spendableCents > 0 && (
            <span className="muted"> · {t('ssa.budget.lasts', { count: daysCovered(settings.spendableCents, daily) })}</span>
          )}
        </dd>
      </dl>

      <ul className="small ssa-rules">
        {/* 売上（payouts）を見せない間は「売上から移した分」を言わない（launchHidden.ts・wordsPass.test.ts） */}
        <li>{t(isMoneyNavHidden('earnings') ? 'ssa.budget.whichLaunch' : 'ssa.budget.which')}</li>
        <li>{t('ssa.budget.ownGames')}</li>
        <li>{t('ssa.budget.charged')}</li>
      </ul>

      {settings && balanceShort(settings.spendableCents, daily) && (
        <p className="alert alert-warn">
          <span>{t('ssa.budget.short', { amount: money(daily, 'usd') })}</span>{' '}
          <Link to="/money/prepaid">{t('ssa.budget.addFunds')}</Link>
        </p>
      )}
      {err && <p className="alert alert-error">{err}</p>}
    </StepCard>
  );
}

// ------------------------------------------------------------------ ④ 確認して出す

function StepReview({
  settings,
  campaign,
  onBack,
  onEdit,
  onSubmitted
}: {
  settings: SelfAdSettings | null;
  campaign: Campaign;
  onBack: () => void;
  onEdit: (n: WizardStep) => void;
  onSubmitted: (c: Campaign) => void;
}) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<{ text: string; reasons: string[] } | null>(null);
  const price = campaign.priceCentsPerMille;

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      onSubmitted((await submitCampaign(campaign.id)).campaign);
    } catch (e) {
      const ae = e as ApiError;
      const reasons = Array.isArray(ae.details?.reasons) ? (ae.details.reasons as string[]) : [];
      setErr({ text: errorText(ae, SSA_ERRORS), reasons });
    } finally {
      setBusy(false);
    }
  };

  const edit = (n: WizardStep) => (
    <button type="button" className="link-button small" onClick={() => onEdit(n)}>
      {t('ssa.review.change')}
    </button>
  );

  return (
    <StepCard
      title={t('ssa.review.title')}
      actions={
        <>
          <button type="button" className="btn btn-ghost" onClick={onBack}>
            {t('ssa.back')}
          </button>
          <button type="button" className="btn btn-primary" disabled={busy || settings?.allStopped} onClick={() => void submit()}>
            {busy ? t('ssa.review.sending') : t('ssa.review.send')}
          </button>
        </>
      }
    >
      <div className="ad-banners">
        <AdBanner campaignId={campaign.id} size={320} assetId={campaign.banner320AssetId} tagline={campaign.tagline} name={campaign.targetName} />
        <AdBanner campaignId={campaign.id} size={728} assetId={campaign.banner728AssetId} tagline={campaign.tagline} name={campaign.targetName} />
      </div>
      <dl className="kv ssa-review-kv">
        <dt>{t('ssa.review.game')}</dt>
        <dd>
          {campaign.targetName} <span className="mono small muted">{campaign.targetUrl}</span>
        </dd>
        <dt>{t('ssa.banner.line')}</dt>
        <dd>
          {campaign.tagline || <span className="muted">{campaign.targetName}</span>} {edit(2)}
        </dd>
        <dt>{t('ssa.budget.daily')}</dt>
        <dd>
          {t('ssa.budget.option', { amount: money(campaign.dailyCapCents, 'usd'), views: viewsFor(campaign.dailyCapCents, price) })} {edit(3)}
        </dd>
        <dt>{t('ssa.budget.total')}</dt>
        <dd>
          {campaign.totalCapCents === null
            ? t('ssa.budget.noLimit')
            : t('ssa.budget.totalOption', { amount: money(campaign.totalCapCents, 'usd'), views: viewsFor(campaign.totalCapCents, price) })}{' '}
          {edit(3)}
        </dd>
      </dl>

      <h3 className="ssa-sub">{t('ssa.review.nextTitle')}</h3>
      <ol className="steps small">
        <li>{t('ssa.review.next1')}</li>
        <li>{t('ssa.review.next2')}</li>
        <li>{t('ssa.review.next3')}</li>
      </ol>

      {err && (
        <div className="alert alert-error" role="alert">
          <span>{err.text}</span>
          {err.reasons.length > 0 && (
            <ul className="small">
              {err.reasons.map((r) => (
                <li key={r}>{isKnownReason(r) ? t(`ssa.reason.${r}`) : r}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </StepCard>
  );
}
